import React, { useState, useEffect, useRef } from 'react';
import { MessageSquareText, Send, RefreshCw, X, Sparkles, ArrowRight, ChevronRight } from 'lucide-react';
import { api } from '../lib/api';

const ACCENT = '#f97316';

interface Suggestion {
  id: string;
  priority: 'high' | 'medium' | 'low';
  category: 'optimization' | 'expansion' | 'error_prevention' | 'best_practice';
  title: string;
  description: string;
  action_label: string;
  action_link: string;
  dismissed: boolean;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  suggested_actions?: { label: string; link: string }[];
}

interface Recommendation {
  template_id: string;
  reason: string;
  relevance: 'high' | 'medium' | 'low';
}

const PRIORITY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  high: { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' },
  medium: { bg: '#fffbeb', text: '#d97706', border: '#fde68a' },
  low: { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe' },
};

const CATEGORY_LABELS: Record<string, string> = {
  optimization: 'Optimization',
  expansion: 'Expansion',
  error_prevention: 'Error Prevention',
  best_practice: 'Best Practice',
};

const EXAMPLE_PROMPTS = [
  'How do I sync ConnectWise tickets?',
  'What templates should I install?',
  'Why are my syncs failing?',
  'How do I set up a CSV import?',
  'How do webhooks work?',
];

export default function AdvisorPage() {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const [loadingChat, setLoadingChat] = useState(false);
  const [loadingRecs, setLoadingRecs] = useState(true);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadSuggestions();
    loadRecommendations();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function loadSuggestions() {
    setLoadingSuggestions(true);
    try {
      const res = await api.get<{ suggestions: Suggestion[] }>('/advisor/suggestions');
      setSuggestions(res.suggestions);
    } catch {
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  }

  async function loadRecommendations() {
    setLoadingRecs(true);
    try {
      const res = await api.get<{ recommendations: Recommendation[] }>('/advisor/recommendations');
      setRecommendations(res.recommendations);
    } catch {
      setRecommendations([]);
    } finally {
      setLoadingRecs(false);
    }
  }

  async function dismissSuggestion(id: string) {
    setSuggestions(prev => prev.filter(s => s.id !== id));
    try {
      await api.post('/advisor/dismiss', { suggestion_id: id });
    } catch {
      // Ignore
    }
  }

  async function sendMessage(question?: string) {
    const q = question || input.trim();
    if (!q) return;

    const userMsg: ChatMessage = { role: 'user', content: q, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoadingChat(true);

    try {
      const res = await api.post<{ answer: string; suggested_actions: { label: string; link: string }[] }>('/advisor/ask', { question: q });
      const assistantMsg: ChatMessage = {
        role: 'assistant',
        content: res.answer,
        timestamp: new Date().toISOString(),
        suggested_actions: res.suggested_actions,
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setLoadingChat(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  return (
    <div style={{ display: 'flex', gap: '1.5rem' }}>
      {/* Main content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <div style={{
            width: 40, height: 40, borderRadius: '0.5rem',
            background: `linear-gradient(135deg, ${ACCENT}, #ea580c)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <MessageSquareText size={22} color="#fff" />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>AI Integration Advisor</h1>
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>Personalized suggestions and help for your integrations</p>
          </div>
        </div>

        {/* Suggestions Panel */}
        <div style={{
          background: '#fff', borderRadius: '0.75rem', border: '1px solid #e5e7eb',
          padding: '1.25rem', marginBottom: '1.5rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: '#111827' }}>Suggestions</h2>
            <button
              onClick={loadSuggestions}
              disabled={loadingSuggestions}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.375rem',
                padding: '0.375rem 0.75rem', borderRadius: '0.375rem',
                border: '1px solid #d1d5db', background: '#fff',
                fontSize: '0.8125rem', color: '#374151', cursor: 'pointer',
              }}
            >
              <RefreshCw size={14} style={loadingSuggestions ? { animation: 'spin 1s linear infinite' } : undefined} />
              Refresh
            </button>
          </div>

          {loadingSuggestions ? (
            <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>Analyzing your integrations...</p>
          ) : suggestions.length === 0 ? (
            <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>No suggestions at this time. Your setup looks good!</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {suggestions.map(s => {
                const colors = PRIORITY_COLORS[s.priority];
                return (
                  <div key={s.id} style={{
                    padding: '1rem', borderRadius: '0.5rem',
                    border: `1px solid ${colors.border}`, background: colors.bg,
                    position: 'relative',
                  }}>
                    <button
                      onClick={() => dismissSuggestion(s.id)}
                      style={{
                        position: 'absolute', top: '0.5rem', right: '0.5rem',
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#9ca3af', padding: '0.25rem',
                      }}
                      title="Dismiss"
                    >
                      <X size={16} />
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem' }}>
                      <span style={{
                        fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase',
                        padding: '0.125rem 0.5rem', borderRadius: '9999px',
                        background: colors.text, color: '#fff',
                      }}>
                        {s.priority}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {CATEGORY_LABELS[s.category]}
                      </span>
                    </div>
                    <h3 style={{ margin: '0 0 0.25rem', fontSize: '0.9375rem', fontWeight: 600, color: '#111827' }}>
                      {s.title}
                    </h3>
                    <p style={{ margin: '0 0 0.75rem', fontSize: '0.8125rem', color: '#4b5563', lineHeight: 1.5 }}>
                      {s.description}
                    </p>
                    <a
                      href={s.action_link}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                        padding: '0.375rem 0.75rem', borderRadius: '0.375rem',
                        background: ACCENT, color: '#fff', textDecoration: 'none',
                        fontSize: '0.8125rem', fontWeight: 500,
                      }}
                    >
                      {s.action_label}
                      <ArrowRight size={14} />
                    </a>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Chat Panel */}
        <div style={{
          background: '#fff', borderRadius: '0.75rem', border: '1px solid #e5e7eb',
          padding: '1.25rem', display: 'flex', flexDirection: 'column',
        }}>
          <h2 style={{ margin: '0 0 1rem', fontSize: '1.125rem', fontWeight: 600, color: '#111827' }}>
            Ask the Advisor
          </h2>

          {/* Messages */}
          <div style={{
            minHeight: 300, maxHeight: 500, overflowY: 'auto',
            marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem',
          }}>
            {messages.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
                <Sparkles size={32} color={ACCENT} style={{ margin: '0 auto 0.75rem' }} />
                <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 1rem' }}>
                  Ask me anything about your integrations. Try one of these:
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center' }}>
                  {EXAMPLE_PROMPTS.map(prompt => (
                    <button
                      key={prompt}
                      onClick={() => sendMessage(prompt)}
                      style={{
                        padding: '0.375rem 0.75rem', borderRadius: '9999px',
                        border: '1px solid #e5e7eb', background: '#f9fafb',
                        fontSize: '0.8125rem', color: '#374151', cursor: 'pointer',
                      }}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, i) => (
                <div key={i} style={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}>
                  <div style={{
                    maxWidth: '80%', padding: '0.75rem 1rem', borderRadius: '0.75rem',
                    background: msg.role === 'user' ? '#2563eb' : '#f3f4f6',
                    color: msg.role === 'user' ? '#fff' : '#111827',
                    fontSize: '0.875rem', lineHeight: 1.5,
                  }}>
                    <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{msg.content}</p>
                    {msg.suggested_actions && msg.suggested_actions.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginTop: '0.625rem' }}>
                        {msg.suggested_actions.map((action, j) => (
                          <a
                            key={j}
                            href={action.link}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                              padding: '0.25rem 0.625rem', borderRadius: '9999px',
                              background: 'rgba(249,115,22,0.15)', color: ACCENT,
                              textDecoration: 'none', fontSize: '0.75rem', fontWeight: 500,
                            }}
                          >
                            {action.label}
                            <ChevronRight size={12} />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
            {loadingChat && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{
                  padding: '0.75rem 1rem', borderRadius: '0.75rem',
                  background: '#f3f4f6', color: '#6b7280', fontSize: '0.875rem',
                }}>
                  Thinking...
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about integrations, syncing, errors..."
              style={{
                flex: 1, padding: '0.625rem 0.875rem', borderRadius: '0.5rem',
                border: '1px solid #d1d5db', fontSize: '0.875rem',
                outline: 'none',
              }}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loadingChat}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 40, height: 40, borderRadius: '0.5rem',
                background: input.trim() ? ACCENT : '#e5e7eb',
                border: 'none', cursor: input.trim() ? 'pointer' : 'default',
                color: '#fff',
              }}
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Right sidebar */}
      <div style={{ width: 300, flexShrink: 0 }}>
        <div style={{
          background: '#fff', borderRadius: '0.75rem', border: '1px solid #e5e7eb',
          padding: '1.25rem', position: 'sticky', top: '5rem',
        }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600, color: '#111827' }}>
            Recommended Templates
          </h3>

          {loadingRecs ? (
            <p style={{ color: '#9ca3af', fontSize: '0.8125rem' }}>Loading...</p>
          ) : recommendations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
              <p style={{ color: '#9ca3af', fontSize: '0.8125rem', margin: '0 0 0.75rem' }}>
                No recommendations yet. Add a connection to get personalized suggestions.
              </p>
              <a
                href="/connections/new"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                  padding: '0.375rem 0.75rem', borderRadius: '0.375rem',
                  background: ACCENT, color: '#fff', textDecoration: 'none',
                  fontSize: '0.8125rem', fontWeight: 500,
                }}
              >
                Add Connection
                <ArrowRight size={14} />
              </a>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {recommendations.map((rec, i) => {
                const relColors = PRIORITY_COLORS[rec.relevance === 'high' ? 'high' : rec.relevance === 'medium' ? 'medium' : 'low'];
                return (
                  <div key={i} style={{
                    padding: '0.75rem', borderRadius: '0.375rem',
                    border: '1px solid #e5e7eb', background: '#fafafa',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.375rem' }}>
                      <span style={{
                        fontSize: '0.625rem', fontWeight: 600, textTransform: 'uppercase',
                        padding: '0.0625rem 0.375rem', borderRadius: '9999px',
                        background: relColors.text, color: '#fff',
                      }}>
                        {rec.relevance}
                      </span>
                    </div>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.8125rem', color: '#4b5563', lineHeight: 1.4 }}>
                      {rec.reason}
                    </p>
                    <a
                      href={`/templates?highlight=${rec.template_id}`}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                        fontSize: '0.75rem', color: ACCENT, textDecoration: 'none', fontWeight: 500,
                      }}
                    >
                      Install Template
                      <ChevronRight size={12} />
                    </a>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '1.25rem', paddingTop: '1.25rem' }}>
            <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>
              Popular with MSPs like you
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {[
                { label: 'CW Manage Ticket Sync', link: '/templates' },
                { label: 'IT Glue Password Import', link: '/templates' },
                { label: 'Company Dedup Flow', link: '/templates' },
              ].map((item, i) => (
                <a
                  key={i}
                  href={item.link}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '0.5rem 0.625rem', borderRadius: '0.375rem',
                    background: '#f3f4f6', textDecoration: 'none',
                    fontSize: '0.8125rem', color: '#374151',
                  }}
                >
                  {item.label}
                  <ChevronRight size={14} color="#9ca3af" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
